import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Search, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { agentsService } from '@/services/agentsService'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import type { AgentAvailableDocument, DocumentPermission } from '@/types/agent'

type SelectorLabels = {
  selected: string
  searchPlaceholder: string
  loading: string
  empty: string
  category: string
  subcategory: string
}

interface AgentDocumentsSelectorProps {
  selectedDocumentIds: number[]
  onChange: (documentIds: number[]) => void
  onCatalogUpdate?: (items: AgentAvailableDocument[]) => void
  disabled?: boolean
  listHeightClass?: string
  directoryId?: number | null
  documentTypes?: string[]
  labels?: Partial<SelectorLabels>
  /** With these, each document gets its permission instead of a checkbox
   *  (permisos-sobre-knowledge, 7.1). The ids are the keys. */
  permissions?: Record<number, DocumentPermission>
  onPermissionsChange?: (permissions: Record<number, DocumentPermission>) => void
}

// Restricted is not offered here: for an agent it did what «No lo ve» does, and
// the one it stops -- the Brain -- has no editor. It lives on the document, as
// «Ocultar al Brain» (Daniel, 2026-09-30).
const PERMISSIONS: DocumentPermission[] = ['leer', 'editar']
const NONE = 'none'
const PERMISSION_LABEL: Record<string, string> = {
  [NONE]: 'agent_form.permisos.ninguno',
  leer: 'agent_form.permisos.leer',
  editar: 'agent_form.permisos.editar',
}

const DEFAULT_LABELS: SelectorLabels = {
  selected: 'Seleccionados',
  searchPlaceholder: 'Buscar documento...',
  loading: 'Cargando documentos...',
  empty: 'No hay documentos para este filtro.',
  category: 'Categoría',
  subcategory: 'Subcategoría',
}

const PAGE_SIZE = 5

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'No se pudieron cargar los documentos.'
}

export function AgentDocumentsSelector({
  selectedDocumentIds,
  onChange,
  onCatalogUpdate,
  disabled = false,
  listHeightClass = 'h-[42vh]',
  directoryId = null,
  documentTypes,
  labels,
  permissions,
  onPermissionsChange,
}: AgentDocumentsSelectorProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const ui = { ...DEFAULT_LABELS, ...labels }

  const [query, setQuery] = React.useState('')
  const [debouncedQuery, setDebouncedQuery] = React.useState('')
  const [documents, setDocuments] = React.useState<AgentAvailableDocument[]>([])
  const [page, setPage] = React.useState(1)
  const [hasMore, setHasMore] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const requestIdRef = React.useRef(0)

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, 250)
    return () => window.clearTimeout(timeoutId)
  }, [query])

  const loadDocuments = React.useCallback(async (targetPage: number, reset: boolean) => {
    const requestId = ++requestIdRef.current
    setError(null)
    if (reset) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    try {
      const response = await agentsService.listAvailableDocuments({
        search: debouncedQuery || undefined,
        page: targetPage,
        pageSize: PAGE_SIZE,
        status: 'embedded',
        directoryId: directoryId ?? undefined,
        documentTypes: documentTypes && documentTypes.length ? documentTypes : undefined,
        cancelKey: 'agent-documents-selector',
        cancelPrevious: true,
      })

      if (requestIdRef.current !== requestId) return

      setDocuments((prev) => {
        const incoming = Array.isArray(response.items) ? response.items : []
        const next = reset ? incoming : [...prev, ...incoming]
        const unique = new Map<number, AgentAvailableDocument>()
        for (const item of next) unique.set(item.id, item)
        const values = Array.from(unique.values())
        onCatalogUpdate?.(values)
        return values
      })
      setPage(response.page)
      setHasMore(response.has_next)
    } catch (loadError) {
      if (requestIdRef.current !== requestId) return
      setError(toErrorMessage(loadError))
      if (reset) {
        setDocuments([])
        setHasMore(false)
      }
    } finally {
      const isLatestRequest = requestIdRef.current === requestId
      if (isLatestRequest) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [debouncedQuery, directoryId, documentTypes, onCatalogUpdate])

  React.useEffect(() => {
    void loadDocuments(1, true)
  }, [loadDocuments])

  const loadMore = React.useCallback(() => {
    if (loading || loadingMore || !hasMore) return
    void loadDocuments(page + 1, false)
  }, [hasMore, loadDocuments, loading, loadingMore, page])

  const { loadingRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    loading: loading || loadingMore,
    threshold: 0.1,
    rootMargin: '140px',
  })

  const toggleDocument = (documentId: number, checked: boolean) => {
    const next = new Set(selectedDocumentIds)
    if (checked) next.add(documentId)
    else next.delete(documentId)
    onChange(Array.from(next))
  }

  const folderOptions = React.useMemo(() => {
    const map = new Map<string, string>()
    documents.forEach((document) => {
      if (document.directory === null || document.directory === undefined) return
      const key = String(document.directory)
      if (!map.has(key)) {
        map.set(key, document.directory_name || `Folder ${key}`)
      }
    })
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [documents])

  const categoryOptions = React.useMemo(() => {
    const set = new Set<string>()
    documents.forEach((document) => {
      if (document.category_name) set.add(document.category_name)
    })
    return Array.from(set)
  }, [documents])

  const selectAllLoaded = () => {
    const next = new Set(selectedDocumentIds)
    documents.forEach((document) => next.add(document.id))
    onChange(Array.from(next))
  }

  const clearSelection = () => {
    onChange([])
  }

  const selectByFolder = (folderId: string) => {
    const next = new Set(selectedDocumentIds)
    documents.forEach((document) => {
      if (String(document.directory ?? '') === folderId) next.add(document.id)
    })
    onChange(Array.from(next))
  }

  const selectByCategory = (categoryName: string) => {
    const next = new Set(selectedDocumentIds)
    documents.forEach((document) => {
      if (document.category_name === categoryName) next.add(document.id)
    })
    onChange(Array.from(next))
  }

  const setPermission = (documentId: number, value: string) => {
    if (!permissions || !onPermissionsChange) return
    const next = { ...permissions }
    if (value === NONE) delete next[documentId]
    else next[documentId] = value as DocumentPermission
    onPermissionsChange(next)
    onChange(Object.keys(next).map(Number))
  }

  if (permissions && onPermissionsChange) {
    return (
      <PermissionList
        documents={documents}
        permissions={permissions}
        onPermission={setPermission}
        onBulk={(ids, value) => {
          const next = { ...permissions }
          ids.forEach((id) => {
            if (value === NONE) delete next[id]
            else next[id] = value as DocumentPermission
          })
          onPermissionsChange(next)
          onChange(Object.keys(next).map(Number))
        }}
        disabled={disabled}
        loading={loading}
        loadingMore={loadingMore}
        loadingRef={loadingRef}
        error={error}
        query={query}
        setQuery={setQuery}
        listHeightClass={listHeightClass}
        t={t}
      />
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <Badge variant="secondary">{selectedDocumentIds.length} {ui.selected.toLowerCase()}</Badge>
      </div>

      <div className={cn("flex items-center gap-2", isMobile ? "flex-nowrap" : "flex-wrap")}>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={selectAllLoaded}
          disabled={disabled || loading || documents.length === 0}
          className={cn(isMobile && "text-[11px] whitespace-nowrap")}
        >
          {isMobile ? 'All' : 'Add all docs'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={clearSelection}
          disabled={disabled || selectedDocumentIds.length === 0}
          className={cn(isMobile && "text-[11px] whitespace-nowrap")}
        >
          Clear
        </Button>
        <Select onValueChange={selectByFolder}>
          <SelectTrigger className={cn("h-8", isMobile ? "w-[92px] px-2 text-[11px]" : "w-[180px]")}>
            <SelectValue placeholder={isMobile ? "Folder" : "Add by folder"} />
          </SelectTrigger>
          <SelectContent>
            {folderOptions.length === 0 ? (
              <SelectItem value="__none" disabled>No folders</SelectItem>
            ) : (
              folderOptions.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <Select onValueChange={selectByCategory}>
          <SelectTrigger className={cn("h-8", isMobile ? "w-[96px] px-2 text-[11px]" : "w-[180px]")}>
            <SelectValue placeholder={isMobile ? "Category" : "Add by category"} />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.length === 0 ? (
              <SelectItem value="__none" disabled>No categories</SelectItem>
            ) : (
              categoryOptions.map((category) => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={ui.searchPlaceholder}
          className="pl-9"
          disabled={disabled || loading}
        />
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      <div className={cn('rounded-lg bg-muted/20', listHeightClass)}>
        <ScrollArea className="h-full p-2">
          {loading ? (
            <div className="p-3 text-sm text-muted-foreground">{ui.loading}</div>
          ) : documents.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">{ui.empty}</div>
          ) : (
            <div className="space-y-2">
              {documents.map((document) => {
                const checked = selectedDocumentIds.includes(document.id)
                return (
                  <Label
                    key={document.id}
                    className="flex cursor-pointer items-start gap-2 rounded-lg border border-transparent px-3 py-2 hover:border-border hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(valor) => toggleDocument(document.id, valor === true)}
                      disabled={disabled}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="truncate text-sm font-medium">{document.title}</p>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                          {document.document_type.toUpperCase()}
                        </Badge>
                        {document.category_name && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                            {ui.category}: {document.category_name}
                          </Badge>
                        )}
                        {document.subcategory_name && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
                            {ui.subcategory}: {document.subcategory_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </Label>
                )
              })}
              <div ref={loadingRef} className="h-8">
                {loadingMore && <p className="px-3 text-xs text-muted-foreground">{ui.loading}</p>}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  )
}

type Translate = ReturnType<typeof useTranslation>['t']

/** «Qué puede consultar»: every document with what this agent may do with it,
 *  a bar to give one permission to a whole folder or category at once, and the
 *  rule for the rest written underneath (permisos-sobre-knowledge, 7.1). */
function PermissionList({
  documents,
  permissions,
  onPermission,
  onBulk,
  disabled,
  loading,
  loadingMore,
  loadingRef,
  error,
  query,
  setQuery,
  listHeightClass,
  t,
}: {
  documents: AgentAvailableDocument[]
  permissions: Record<number, DocumentPermission>
  onPermission: (documentId: number, value: string) => void
  onBulk: (documentIds: number[], value: string) => void
  disabled: boolean
  loading: boolean
  loadingMore: boolean
  loadingRef: React.Ref<HTMLDivElement>
  error: string | null
  query: string
  setQuery: (value: string) => void
  listHeightClass: string
  t: Translate
}) {
  const [group, setGroup] = React.useState('')
  const [bulkValue, setBulkValue] = React.useState<string>('leer')
  const [everything, setEverything] = React.useState<AgentAvailableDocument[]>([])
  const [applying, setApplying] = React.useState(false)

  // The whole catalogue, not the page on screen: a folder is all of it.
  React.useEffect(() => {
    let alive = true
    agentsService
      .listAllAvailableDocuments({ status: 'embedded' })
      .then((items) => alive && setEverything(items))
      .catch(() => alive && setEverything([]))
    return () => {
      alive = false
    }
  }, [])

  const groups = React.useMemo(() => {
    const found = new Map<string, string>()
    everything.forEach((document) => {
      if (document.directory != null) {
        found.set(`dir:${document.directory}`, t('agent_form.permisos.carpeta', { nombre: document.directory_path || document.directory_name || document.directory }))
      }
      if (document.category_name) {
        found.set(`cat:${document.category_name}`, t('agent_form.permisos.categoria', { nombre: document.category_name }))
      }
    })
    return Array.from(found.entries()).map(([value, label]) => ({ value, label }))
  }, [everything, t])

  const inGroup = (document: AgentAvailableDocument) =>
    group.startsWith('dir:')
      ? String(document.directory ?? '') === group.slice(4)
      : document.category_name === group.slice(4)

  const apply = () => {
    setApplying(true)
    onBulk(everything.filter(inGroup).map((document) => document.id), bulkValue)
    setApplying(false)
  }

  const label = (value: string) => t(PERMISSION_LABEL[value] ?? PERMISSION_LABEL[NONE])

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={group} onValueChange={setGroup} disabled={disabled || groups.length === 0}>
          <SelectTrigger className="w-full sm:w-64" aria-label={t('agent_form.permisos.aplicar_a')}>
            <SelectValue placeholder={groups.length ? t('agent_form.permisos.aplicar_a') : t('agent_form.permisos.sin_grupos')} />
          </SelectTrigger>
          <SelectContent>
            {groups.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={bulkValue} onValueChange={setBulkValue} disabled={disabled}>
          <SelectTrigger className="w-40" aria-label={t('agent_form.permisos.permiso')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...PERMISSIONS, NONE].map((value) => (
              <SelectItem key={value} value={value}>{label(value)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" onClick={apply} disabled={disabled || !group || applying}>
          {t('agent_form.permisos.aplicar')}
        </Button>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('agent_form.permisos.buscar')}
          aria-label={t('agent_form.permisos.buscar')}
          className="pl-9"
          disabled={disabled || loading}
        />
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className={listHeightClass}>
        <ScrollArea className="h-full">
          {loading ? (
            <p className="p-2 text-sm text-muted-foreground">{t('agent_form.permisos.cargando')}</p>
          ) : documents.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">{t('agent_form.permisos.vacio')}</p>
          ) : (
            <div className="space-y-1">
              {documents.map((document) => {
                const value: string = permissions[document.id] ?? NONE
                return (
                  <div key={document.id} className="flex items-center gap-2 px-2 py-1">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{document.title}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[document.directory_path || document.directory_name, document.category_name].filter(Boolean).join(' · ') || document.document_type.toUpperCase()}
                      </p>
                    </div>
                    <Select value={value} onValueChange={(next) => onPermission(document.id, next)} disabled={disabled}>
                      <SelectTrigger
                        className={cn('w-40 shrink-0', value === NONE && 'text-muted-foreground')}
                        aria-label={t('agent_form.permisos.permiso_de', { titulo: document.title })}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[NONE, ...PERMISSIONS].map((option) => (
                          <SelectItem key={option} value={option}>{label(option)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )
              })}
              <div ref={loadingRef} className="h-8">
                {loadingMore && <p className="px-2 text-xs text-muted-foreground">{t('agent_form.permisos.cargando')}</p>}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>

      <p className="text-xs text-muted-foreground">{t('agent_form.permisos.regla')}</p>
    </div>
  )
}
